import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Footer from '../homepage/components/Footer';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ResourceSearch from '../../components/ui/ResourceSearch';
import { blogService } from '../../services/blogService';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import Input from '../../components/ui/Input';
import TierUpgradeModal from '../seller-account-dashboard/components/TierUpgradeModal';
import { sellerService } from '../../services/sellerService';

const ServicesGarage = () => {
  const navigate = useNavigate();
  const [blogPosts, setBlogPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState('all');
  const [loading, setLoading] = useState(true);

  const { user, refreshUserProfile } = useAuth(); // Get currently authenticated user if present
  
  // Reusable 3-Card plan modal states
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [upgradeShopType, setUpgradeShopType] = useState('repair'); // 'repair' | 'wrap'
  const [upgradeAccountId, setUpgradeAccountId] = useState(null);
  const [upgradeCurrentTier, setUpgradeCurrentTier] = useState('repair_entry');

  // Automatically ensure a seller account exists for logged-in user
  const ensureSellerAccount = async () => {
    try {
      const { data: existing } = await supabase
        .from('seller_accounts')
        .select('id, tier')
        .eq('user_id', user.id)
        .maybeSingle();
        
      if (existing) {
        return existing;
      }
      
      // Auto-create a standard default seller account row first if none exists
      const { data: newAcc, error } = await supabase
        .from('seller_accounts')
        .insert({
          user_id: user.id,
          business_name: `${user.email.split('@')[0]}'s Services Garage`,
          business_email: user.email,
          tier: 'entry', // default entry seller
          account_status: 'Active',
          subscription_status: 'active',
          monthly_fee: 14.99,
          subscription_start_date: new Date().toISOString()
        })
        .select()
        .single();
        
      if (error) throw error;
      
      // Assign standard Seller role
      const { data: sellerRole } = await supabase.from('roles').select('id').eq('name', 'Seller').single();
      if (sellerRole) {
        await supabase.from('user_roles').insert({ user_id: user.id, role_id: sellerRole.id }).select().maybeSingle();
      }
      
      return newAcc;
    } catch (e) {
      console.error('Failed to ensure seller account:', e);
      return null;
    }
  };

  const [pageSettings, setPageSettings] = useState({
    title: 'Services Garage Network',
    subtitle: 'High-tech repair and wrap platform providing automotive shops and custom wrap studios with state-of-the-art 3D visualizers, real-time client approval boards, and integrated parts verification.',
    cta_title: 'Ready to modernize your shop?',
    cta_subtitle: 'Register your garage or wrap studio today to unlock interactive 3D customer diagnostics, automated parts audits, and secure credit card checkouts.'
  });

  const getPageIcon = (page) => {
    switch (page) {
      case 'seller-toolbox': return 'Store';
      case 'documentation': return 'BookOpen';
      case 'api-reference': return 'Code';
      case 'tutorials': return 'Video';
      case 'resources-hub': return 'Globe';
      default: return 'Wrench';
    }
  };

  useEffect(() => {
    const loadPageData = async () => {
      try {
        setLoading(true);
        const [posts, cats] = await Promise.all([
          blogService.getPublishedPosts({ targetPage: 'seller-toolbox', limit: 12 }),
          blogService.getCategories()
        ]);
        setBlogPosts(posts);
        setCategories(cats || []);
      } catch (error) {
        console.error('Error fetching Services Garage data:', error);
      } finally {
        setLoading(false);
      }
    };
    loadPageData();
  }, []);
  
  const filteredPosts = activeCategory === 'all' 
    ? blogPosts 
    : blogPosts.filter(post => post.category?.id === activeCategory);

  const garageSections = [
    {
      id: 'diagnostics-3d',
      title: 'Interactive 3D Diagnostics Ledger',
      icon: 'Activity',
      description: 'Isolate parts, log service records, and build visual diagnostic charts directly on a 3D digital-twin.',
      features: [
        {
          title: 'Mechanical Visualizer',
          items: [
            'Direct 3D model rendering with custom lighting controls',
            'Symmetrical part isolation, hiding, wireframing, and explosions',
            'Interactive hover tooltips with automated parts inventory mapping',
            'Dynamic QR report generation linked directly to shop invoices'
          ]
        },
        {
          title: 'Digital Service Ledger',
          items: [
            'Log repairs, parts replacement, and fluid services in real-time',
            'Assign custom diagnostic codes and mechanical statuses',
            'Set up detailed estimate entries with separate parts and labor hours',
            'Store historical customer service history records securely'
          ]
        }
      ]
    },
    {
      id: 'livery-wrap',
      title: '3D Wrap Studio',
      icon: 'Palette',
      description: 'Configure and apply custom paint wraps, gloss finishes, and graphic decals with before and after views.',
      features: [
        {
          title: 'Design Controls',
          items: [
            'PBR metallic reflections, roughness, and satin gloss sliders',
            'Instant PNG/SVG decal graphics positioning, scaling, and rotations',
            'Symmetrical Before (Original) and After (Wrapped) view toggles',
            'Project-specific original templates to match factory specs'
          ]
        },
        {
          title: 'Client Previews',
          items: [
            'Export and share secure, interactive 3D wrap previews with clients',
            'Interactive client sidebar showcasing layer-by-layer specifications',
            'Real-time photorealistic Shaded canvas rendering by default',
            'Bi-directional list-to-viewport selection and mesh highlighting'
          ]
        }
      ]
    },
    {
      id: 'approvals-payments',
      title: 'Secure Client Approvals & Payments',
      icon: 'ShieldCheck',
      description: 'Symmetrical estimate confirmation portals and integrated secure credit card payments.',
      features: [
        {
          title: 'Estimate Approval Board',
          items: [
            'Digital customer portal displaying real-time repair estimates',
            'Let clients accept or decline individual line items on-the-fly',
            'Automatic total recalculation based on approved parts and labor',
            'Clear authorization tracking and digital signature capabilities'
          ]
        },
        {
          title: 'Secure Payments',
          items: [
            'Secure credit card from design approval to checkout',
            'Instantly deposit service payments directly to your shop merchant account',
            'Automated email receipts and invoices generated upon checkout',
            'Fraud protection and card vault secure storage compliant standards'
          ]
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      
      {/* Hero Section - DARK CONTRASTING BACKGROUND */}
      <div className="bg-slate-900 text-white py-24 border-b border-slate-800">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-indigo-600 rounded-2xl mb-6 shadow-lg shadow-indigo-500/20">
              <Icon name="Wrench" size={40} className="text-white" />
            </div>
            <h1 className="text-5xl font-extrabold mb-6 tracking-tight">{pageSettings.title}</h1>
            <p className="text-xl text-slate-300 mb-10 leading-relaxed font-medium">
              {pageSettings.subtitle}
            </p>
            <div className="flex justify-center gap-4 mb-12">
              {user ? (
                <Button 
                  variant="default" 
                  className="bg-indigo-600 hover:bg-indigo-700 text-white border-none px-8 py-3 font-bold"
                  onClick={() => {
                    const el = document.getElementById('shop-upgrade-onboarding');
                    el?.scrollIntoView({ behavior: 'smooth' });
                  }}
                >
                  Configure & Activate Your Shop
                </Button>
              ) : (
                <Button 
                  variant="default" 
                  className="bg-indigo-600 hover:bg-indigo-700 text-white border-none px-8 py-3"
                  onClick={() => navigate('/signup')}
                >
                  Register Your Shop
                </Button>
              )}
              <Button 
                variant="outline" 
                className="border-slate-700 text-white hover:bg-slate-800 px-8 py-3"
                onClick={() => {
                  const section = document.getElementById('diagnostics-3d');
                  section?.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                Explore Capabilities
              </Button>
            </div>

            <ResourceSearch placeholder="Search garage resources, diagnostic guides, wrap tips..." />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-6 py-16">
        {/* Blog / Resource Center Section */}
        <div className="mb-20">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-8 text-left">
            <div>
              <h2 className="text-3xl font-bold text-slate-800">Garage Resource Center</h2>
              <p className="text-slate-600 mt-2 text-lg">Insights, guides, and strategies to grow your repair shop or custom wrap studio business.</p>
            </div>
            <Link to="/resources-hub" className="text-indigo-600 hover:text-indigo-700 font-bold flex items-center group bg-white px-4 py-2 rounded-lg shadow-sm border border-slate-100 transition-all hover:shadow-md shrink-0">
              View All Articles
              <Icon name="ArrowRight" size={20} className="ml-2 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>

          {/* Category Filter Chips */}
          {!loading && categories.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-8">
              <button
                onClick={() => setActiveCategory('all')}
                className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-all border ${
                  activeCategory === 'all' 
                  ? 'bg-indigo-600 text-white border-indigo-600 shadow-md' 
                  : 'bg-white text-slate-600 border-slate-200 hover:border-indigo-300'
                }`}
              >
                All Topics
              </button>
              {categories.map(cat => (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.id)}
                  className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-all border ${
                    activeCategory === cat.id 
                    ? 'bg-indigo-600 text-white border-indigo-600 shadow-md' 
                    : 'bg-white text-slate-600 border-slate-200 hover:border-indigo-300'
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>
          )}

          {loading ? (
            <div className="grid md:grid-cols-3 gap-8">
              {[1, 2, 3].map(i => (
                <div key={i} className="animate-pulse bg-white rounded-2xl h-80 shadow-sm border border-slate-100" />
              ))}
            </div>
          ) : (
            <div className="grid md:grid-cols-3 gap-8 text-left">
              {filteredPosts?.map((post) => (
                <Link 
                  key={post.id} 
                  to={`/${post.target_page || post.targetPage || 'blog'}/${post.slug}`}
                  className="bg-white rounded-2xl shadow-lg border border-slate-100 overflow-hidden hover:shadow-xl transition-all group flex flex-col h-full"
                >
                  <div className="h-48 bg-slate-100 overflow-hidden relative">
                    {post.featured_image ? (
                      <img src={post.featured_image} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-slate-300">
                        <Icon name={getPageIcon(post.targetPage)} size={48} />
                      </div>
                    )}
                    <div className="absolute top-4 left-4">
                      <span className="px-3 py-1 bg-indigo-600 text-white text-xs font-bold rounded-full uppercase tracking-wider shadow-sm">
                        {post.category?.name}
                      </span>
                    </div>
                  </div>
                  <div className="p-6 flex-1 flex flex-col">
                    <h3 className="text-xl font-bold text-slate-800 mb-3 group-hover:text-indigo-600 transition-colors line-clamp-2">
                      {post.title}
                    </h3>
                    <p className="text-slate-600 text-sm mb-4 line-clamp-3 font-medium leading-relaxed">
                      {post.excerpt}
                    </p>
                    <div className="mt-auto flex items-center text-indigo-600 font-semibold text-sm">
                      Read More
                      <Icon name="ArrowRight" size={16} className="ml-1 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Feature Sections */}
        <div className="space-y-24 mb-16 border-t border-slate-200/60 pt-16">
          {garageSections.map((sec, idx) => {
            return (
              <div key={sec.id} id={sec.id} className={`flex flex-col gap-12 items-center ${idx % 2 === 1 ? 'md:flex-row-reverse' : 'md:flex-row'}`}>
                <div className="w-full md:w-1/2 space-y-5 text-left">
                  <div className="w-12 h-12 rounded-2xl bg-indigo-600/10 text-indigo-600 flex items-center justify-center shadow-sm">
                    <Icon name={sec.icon} size={22} />
                  </div>
                  <h2 className="text-3xl font-black uppercase text-slate-800 tracking-wide">
                    {sec.title}
                  </h2>
                  <p className="text-base text-slate-600 leading-relaxed font-semibold">
                    {sec.description}
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 pt-2">
                    {sec.features.map((feat) => (
                      <div key={feat.title} className="space-y-2.5">
                        <h4 className="font-extrabold text-xs uppercase tracking-wider text-indigo-600">
                          {feat.title}
                        </h4>
                        <ul className="space-y-1.5 text-xs text-slate-500 leading-relaxed font-bold">
                          {feat.items.map((item, i) => (
                            <li key={i} className="flex gap-2">
                              <span className="text-indigo-500 shrink-0">•</span>
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="w-full md:w-1/2 bg-white rounded-3xl p-12 flex items-center justify-center border border-slate-200 aspect-[16/10] overflow-hidden group shadow-md hover:shadow-lg transition-shadow duration-300">
                  <Icon name={sec.icon} size={96} className="text-slate-200 group-hover:text-indigo-600 transition-colors duration-500 animate-pulse-soft" />
                </div>
              </div>
            );
          })}
        </div>

        {/* Call to Action or Subscriptions Onboarding Upgrade Board */}
        {user ? (
          <div id="shop-upgrade-onboarding" className="bg-slate-900 border border-slate-800 rounded-3xl p-8 md:p-12 text-center relative overflow-hidden shadow-2xl mt-12 text-white scroll-mt-6">
            <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-r from-indigo-500/5 to-purple-500/5 pointer-events-none" />
            <div className="max-w-2xl mx-auto">
              <span className="text-[10px] font-black text-indigo-400 uppercase tracking-widest block mb-2">Authenticated Activation Hub</span>
              <h2 className="text-3xl font-black uppercase text-white tracking-wide">
                Activate Your High-Tech Shop
              </h2>
              <p className="text-sm text-slate-300 mt-4 leading-relaxed font-medium max-w-xl mx-auto mb-8">
                Welcome back! Select from the two high-tech service types below to open the complete 3-card pricing builder and choose the subscription tier that best fits your business.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  onClick={async () => {
                    const acc = await ensureSellerAccount();
                    if (acc) {
                      setUpgradeAccountId(acc.id);
                      setUpgradeCurrentTier(acc.repair_tier || acc.repairTier || '');
                      setUpgradeShopType('repair');
                      setUpgradeModalOpen(true);
                    }
                  }}
                  className="bg-indigo-600 hover:bg-indigo-500 text-white font-black uppercase text-xs h-12 px-8 shadow-md"
                >
                  Configure 3D Repair Shop Plans
                </Button>
                
                <Button 
                  onClick={async () => {
                    const acc = await ensureSellerAccount();
                    if (acc) {
                      setUpgradeAccountId(acc.id);
                      setUpgradeCurrentTier(acc.wrap_tier || acc.wrapTier || '');
                      setUpgradeShopType('wrap');
                      setUpgradeModalOpen(true);
                    }
                  }}
                  className="bg-purple-600 hover:bg-purple-500 text-white font-black uppercase text-xs h-12 px-8 shadow-md"
                >
                  Configure 3D Wrap Studio Plans
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-slate-900 border border-slate-800 rounded-3xl p-8 md:p-12 text-center relative overflow-hidden shadow-xl mt-12">
            <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-r from-indigo-500/5 to-purple-500/5 pointer-events-none" />
            <h2 className="text-2xl md:text-4xl font-black uppercase text-white tracking-wide">
              {pageSettings.cta_title}
            </h2>
            <p className="text-sm text-slate-300 mt-4 leading-relaxed font-medium max-w-2xl mx-auto">
              {pageSettings.cta_subtitle}
            </p>
            <div className="mt-8 flex justify-center">
              <Button 
                variant="default" 
                onClick={() => navigate('/signup')} 
                className="bg-indigo-600 hover:bg-indigo-500 text-white font-black uppercase text-xs h-12 px-8 shadow-md"
              >
                Get Started Symmetrically
              </Button>
            </div>
          </div>
        )}
      </div>

      <Footer />

      {/* Complete 3-Card Subscription Upgrade Plan Modal */}
      {upgradeModalOpen && (
        <TierUpgradeModal
          currentTier={upgradeCurrentTier}
          accountId={upgradeAccountId}
          shopType={upgradeShopType}
          isSignUp={true}
          onClose={() => setUpgradeModalOpen(false)}
          onUpgradeSuccess={async (newTier) => {
            // Symmetrically refresh user profile tokens to load newly assigned DB Shop roles immediately!
            if (refreshUserProfile) {
              await refreshUserProfile();
            }
            setUpgradeModalOpen(false);
            navigate('/shop-dashboard');
          }}
        />
      )}
    </div>
  );
};

export default ServicesGarage;